import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import BrandService from '../services/BrandService';

const initialState = {
    data: [],
    datas: [],
    formError: [],
    isSuccess: false,
    submitLoading: false,
    loading: false,
    isOpenUomDialog: false,
    error: null,
};


export const fetchBrandList = createAsyncThunk('brand/fetchBrandList', async (data, { rejectWithValue }) => {
    try {
        const resp = await BrandService.List(data);
        return resp;
    } catch (error) {
        return rejectWithValue(error.response?.data);
    }
});


export const createBrand = createAsyncThunk('brand/createBrand', async (data, { rejectWithValue }) => {
    try {
        const response = await BrandService.Create(data);
        return response;
    } catch (error) {
        return rejectWithValue(error.response);
    }
});


export const updateBrand = createAsyncThunk('brand/updateBrand', async (data, { rejectWithValue }) => {
    try {
        const response = await BrandService.Put(data);
        return response;
    } catch (error) {
        return rejectWithValue(error.response);
    }
});

export const deleteBrand = createAsyncThunk('brand/deleteBrand', async (id, { rejectWithValue }) => {
    try {
        const response = await BrandService.Delt(id);
        return response;
    } catch (error) {
        return rejectWithValue(error.response);
    }
});


export const getAllBrands = createAsyncThunk('brand/getAllBrands', async (_, { rejectWithValue }) => {
    try {
        const response = await BrandService.AllUom();
        return response;
    } catch (error) {
        return rejectWithValue(error.response);
    }
});

const brandSlice = createSlice({
    name: 'brand',
    initialState,
    reducers: {
        openUomDialog: (state, action) => {
            state.isOpenUomDialog = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // 📦 Fetch brand list
            .addCase(fetchBrandList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBrandList.fulfilled, (state, action) => {
                state.loading = false; 
                if (action.payload?.status === 200) {
                    state.datas = action.payload.data?.data?.brands ?? [];
                } else {
                    state.error = action.payload?.data?.message || 'Failed to fetch brands';
                    toast.error(state.error);
                }
            })
            .addCase(fetchBrandList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Error fetching brands';
                toast.error(state.error);
            })

            // ➕ Create
            .addCase(createBrand.pending, (state) => {
                state.submitLoading = true;
                state.isSuccess = false;
                state.formError = [];
            })
            .addCase(createBrand.fulfilled, (state, action) => {
                state.submitLoading = false; 
                state.isSuccess = true;
                if (action.payload?.data?.data) {
                    state.datas.unshift(action.payload.data.data);
                }
                toast.success(action.payload?.data?.message || 'Brand created successfully');
            })
            .addCase(createBrand.rejected, (state, action) => {
                state.submitLoading = false;
                state.isSuccess = false;
                const errors = action.payload?.data?.errors;
                if (isEmpty(errors)) {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                } else {
                    state.formError = errors;
                }
            })

            // ✏️ Update
            .addCase(updateBrand.pending, (state) => {
                state.submitLoading = true;
                state.isSuccess = false;
                state.formError = [];
            })
            .addCase(updateBrand.fulfilled, (state, action) => {
                state.submitLoading = false; // ✅ ensure reset
                state.isSuccess = true;
                const updated = action.payload?.data?.data;
                if (updated) {
                    const index = state.datas.findIndex((b) => b.id === updated.id);
                    if (index !== -1) {
                        state.datas[index] = updated;
                    }
                }
                toast.success(action.payload?.data?.message || 'Brand updated successfully');
            })
            .addCase(updateBrand.rejected, (state, action) => {
                state.submitLoading = false;
                state.isSuccess = false;
                const errors = action.payload?.data?.errors;
                if (isEmpty(errors)) {
                    toast.error(action.payload?.data?.message || 'Something went wrong');
                } else {
                    state.formError = errors;
                }
            })

            // 🗑️ Delete
            .addCase(deleteBrand.pending, (state) => {
                state.submitLoading = true;
            })
            .addCase(deleteBrand.fulfilled, (state, action) => {
                state.submitLoading = false; // ✅ ensure reset
                const deletedId = action.meta.arg;
                state.datas = state.datas.filter((item) => item.id !== deletedId);
                state.isOpenUomDialog = false;
                toast.success(action.payload?.data?.message || 'Brand deleted successfully');
            })
            .addCase(deleteBrand.rejected, (state, action) => {
                state.submitLoading = false;
                toast.error(action.payload?.data?.message || 'Something went wrong');
            })

            // 🟢 Get all active brands
            .addCase(getAllBrands.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllBrands.fulfilled, (state, action) => {
                state.loading = false; // ✅ ensure reset
                if (action.payload?.status === 200) {
                    state.datas = action.payload.data?.data ?? [];
                } else {
                    state.error = action.payload?.data?.message || 'Failed to load active brands';
                    toast.error(state.error);
                }
            })
            .addCase(getAllBrands.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.data?.message || 'Something went wrong';
                toast.error(state.error);
            });
    },
});

export const { openUomDialog } = brandSlice.actions;
export const brandSelector = (state) => state.brand;
export default brandSlice.reducer;
